/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import styled from 'styled-components';

import {
    ModelRecommendations,
    RecommendedModelResult,
} from '@toucan/common/features/recommendations';
import { RecommendationsVariant } from '@toucan/common/services/feature-flags/constants';
import {
    formatModelPrice,
    getModelHrefFromSlug,
} from '@toucan/common/services/model/helpers';

import { DataLayerEventType } from '@toucan/client/global/analytics';
import { MarketContext } from '@toucan/client/global/market';
import { Model } from '@toucan/client/global/model';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { ProductSummaryV2 } from '@toucan/client/global/product';

import { ConstrainedWrapper } from '@toucan/client/shared/components/constrained-wrapper';
import { LinkVariant } from '@toucan/client/shared/components/link';
import { ScrollableCarousel } from '@toucan/client/shared/components/scrollable-carousel';
import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';

import { ModelCard } from '@toucan/client/features/search/components/model-card';

import { DEFAULT_TEST_ID } from './constants';
import { styles } from './model-recommendations-carousel.styles';

export type ModelRecommendationsCarouselProps = {
    modelRecommendations: ModelRecommendations;
    pageModel?: Model;
    pageModelIds?: number[];
    pageProduct?: ProductSummaryV2;
    title?: string;
    'data-testid'?: string;
};

export const StyledModelRecommendationsCarousel = styled(ConstrainedWrapper)`
    ${styles}
`;

export const ModelRecommendationsCarousel: FunctionComponent<
    ModelRecommendationsCarouselProps
> = ({
    modelRecommendations: { isEnabled, results, meta, variant },
    pageModel,
    pageModelIds,
    pageProduct,
    title,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}: ModelRecommendationsCarouselProps): JSX.Element | null => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const { route } = useRouter();

    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        /**
         * This event needs to be added to the datalayer after the "PAGE_VIEW" event
         * Moving it further back in the event loop using setTimeout ensures that it will be called last
         */
        setTimeout(() => {
            trackViaDataLayer({
                event: DataLayerEventType.MODEL_RECOMMENDATIONS_CAROUSEL,
                pageTemplate: route?.replace(/^\//, ''),
                pageModelId: pageModel?.id ?? null,
                pageModelIds: pageModelIds ?? null,
                pageProductId: pageProduct?.id ?? null,
                variation: variant?.name ?? null,
                campaign: variant?.payload?.value ?? null,
                predictionModel: meta?.version ?? null,
                modelIds: results.map(({ id }) => id),
            });
        });
    }, [pageModel?.id, pageProduct?.id]);

    if (
        !isEnabled ||
        !results.length ||
        variant?.name === RecommendationsVariant.CONTROL
    ) {
        return null;
    }

    const formatPrice = formatModelPrice(activeMarket);

    const renderModelCard = ({
        id,
        name,
        images,
        urlSegment,
        priceMinimum,
        priceMaximum,
        stockCount,
    }: RecommendedModelResult) => {
        const linkHref = getModelHrefFromSlug(t, activeMarket.slug, urlSegment);

        return (
            <WrappedLink
                key={id}
                className="model-recommendations-card-link"
                data-testid="link-wrapper"
                variant={LinkVariant.CUSTOM}
                href={`/model?slug=${urlSegment}`}
                as={linkHref}
                passHref
                hasOutlineOnFocus
            >
                <ModelCard
                    className="model-recommendations-card"
                    modelListing={{
                        id,
                        name,
                        linkHref,
                        description: '',
                        image: images[0],
                        linkTemplatePath: `/model?slug=${urlSegment}`,
                        priceMax: formatPrice(priceMaximum),
                        priceMin: formatPrice(priceMinimum),
                        quantity: stockCount,
                    }}
                />
            </WrappedLink>
        );
    };

    return (
        <StyledModelRecommendationsCarousel data-testid={dataTestId}>
            <Heading
                size={HeadingSize.H2}
                className="recommendations-carousel-heading"
            >
                {title ?? t('You might also like')}
            </Heading>
            <ScrollableCarousel
                className="model-recommendations-carousel"
                items={results.map(renderModelCard)}
                showButtons
            />
        </StyledModelRecommendationsCarousel>
    );
};
